import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

interface InvoiceHeaderProps {
  invoice: {
    invoice_number: string | null;
    vendor_id: string | null;
    vendor_name?: string | null;
    total_amount: number;
    currency: string | null;
    invoice_date: string | null;
    service_date: string | null;
    due_date: string | null;
  };
  vendorAddress?: {
    address: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
  } | null;
  priceBook?: {
    id: string;
    name: string;
  } | null;
}

export function InvoiceHeader({ invoice, vendorAddress, priceBook }: InvoiceHeaderProps) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return format(parseISO(dateStr), 'yyyy-MMM-dd');
    } catch {
      return '—';
    }
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatAddress = () => {
    if (!vendorAddress) return null;
    const parts = [
      vendorAddress.address,
      vendorAddress.city,
      vendorAddress.state,
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div className="bg-card border border-border rounded-lg p-5 h-full">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left: Invoice ID & Vendor */}
        <div>
          <h1 className="text-xl font-bold text-foreground">
            INVOICE #{invoice.invoice_number || '—'}
          </h1>
          {invoice.vendor_id ? (
            <Link
              to={`/vendors/${invoice.vendor_id}`}
              className="text-base font-medium text-primary hover:underline mt-1 block"
            >
              {invoice.vendor_name || 'No vendor'}
            </Link>
          ) : (
            <p className="text-base font-medium text-foreground mt-1">
              {invoice.vendor_name || 'No vendor'}
            </p>
          )}
          {formatAddress() && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {formatAddress()}
            </p>
          )}
        </div>

        {/* Middle: Invoice Total & Currency */}
        <div className="space-y-2">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Invoice Total</p>
            <p className="text-lg font-semibold text-foreground">
              {formatCurrency(invoice.total_amount)} {invoice.currency || 'USD'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Price Book</p>
            {priceBook ? (
              <Link 
                to={`/price-books/${priceBook.id}`}
                className="text-sm text-primary hover:underline"
              >
                {priceBook.name}
              </Link>
            ) : (
              <p className="text-sm text-muted-foreground">—</p>
            )}
          </div>
        </div>

        {/* Right: Dates */}
        <div className="space-y-2">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Invoice Date</p>
            <p className="text-sm text-foreground">{formatDate(invoice.invoice_date)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Due Date</p>
            <p className="text-sm text-foreground">{formatDate(invoice.due_date)}</p>
          </div>
        </div>

        {/* Far Right: Service Date */}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Service Date</p>
          <p className="text-sm text-foreground">{formatDate(invoice.service_date)}</p>
        </div>
      </div>
    </div>
  );
}
